import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Banknote, SaudiRiyal, Printer } from "lucide-react";
import { PRINT_HEADER_TITLE } from "../../../utils/printUtils";

import {
  bankTransferSubscribe,
  moduleBankTransferSubscribe,
  upgradeBankTransferSubscribe,
  checkSubscription,
} from "@/modules/subscriptions/store/subscriptionSlice";

const BANK_TRANSFER_STEPS = ["form", "pending", "approved", "rejected"];
const SUBSCRIBE_OVERRIDE_FLAG = "allowSubscribeAfterRejection";
const UPGRADE_BANK_TRANSFER_FLAG = "tarh_upgrade_bank_transfer_active";
const UPGRADE_BANK_TRANSFER_REF = "tarh_upgrade_bank_transfer_ref";

const markSubscribeOverride = () => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(SUBSCRIBE_OVERRIDE_FLAG, "1");
  } catch {
    // ignore storage failures
  }
};

const clearSubscribeOverride = () => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(SUBSCRIBE_OVERRIDE_FLAG);
  } catch {
    // ignore removal failures
  }
};

const readSessionValue = (key) => {
  if (typeof window === "undefined") return null;
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSessionValue = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // ignore storage failures
  }
};

const removeSessionValue = (key) => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore storage failures
  }
};

const buildStepPath = (value) =>
  value === "form" ? "/bank-transfer/form" : `/bank-transfer/${value}`;

const normalizePath = (path) => {
  if (!path) return "/";
  const trimmed = path.replace(/\/+$/, "");
  return trimmed || "/";
};

export default function BankTransferPage() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const navStateRef = useRef(location.state || {});
  const checkingStatusRef = useRef(false);
  const pollDelayRef = useRef(30_000);
  const pollTimeoutRef = useRef(null);
  const focusDebounceRef = useRef(null);

  const user = useSelector((s) => s.auth.user);
  const subscribed = useSelector((s) => s.subscriptions?.subscribed);
  const {
    plans,
    currentPlanId,
    currentDurationId,
    flowState,
    currentPaymentMethod,
    currentPaymentVerificationStatus,
    currentPaymentReference,
  } = useSelector((s) => s.subscriptions);

  const navState = location.state || navStateRef.current || {};
  const navPlan = navState.plan || null;
  const navDuration = navState.duration || null;
  const navModuleIds = Array.isArray(navState.module_ids)
    ? navState.module_ids
    : [];
  const navModuleSummary = navState.module_summary || null;
  const navModuleAmount =
    typeof navState.amount === "number" ? navState.amount : null;
  const navUpgrade = navState.upgrade === true;
  const navResetBankTransferSession = navState.resetBankTransferSession === true;
  const storedUpgradeBankTransfer =
    readSessionValue(UPGRADE_BANK_TRANSFER_FLAG) === "1";
  const storedUpgradePaymentRef = readSessionValue(UPGRADE_BANK_TRANSFER_REF);
  const effectiveStoredUpgradePaymentRef = navResetBankTransferSession
    ? null
    : storedUpgradePaymentRef;
  const isUpgradeBankTransfer = navUpgrade || storedUpgradeBankTransfer;
  const navInitialMessage = navState.initialMessage || null;
  const navInitialSubscription = navState.initialSubscription || null;
  const navForcedStep = navState.forcedStep || null;

  useEffect(() => {
    if (location.state) {
      navStateRef.current = location.state;
    }
  }, [location.state]);

  useEffect(() => {
    if (navUpgrade) {
      writeSessionValue(UPGRADE_BANK_TRANSFER_FLAG, "1");
    }
  }, [navUpgrade]);

  const updateNavState = (partial) => {
    navStateRef.current = {
      ...(navStateRef.current || {}),
      ...partial,
    };
  };

  const [forcedStep, setForcedStep] = useState(navForcedStep || null);

  const setForcedStepValue = (value) => {
    setForcedStep(value || null);
    updateNavState({ forcedStep: value || null });
  };

  const resolvedPlan = useMemo(() => {
    if (navModuleIds.length > 0) return null;
    if (navPlan) return navPlan;
    if (!plans || !plans.length || !currentPlanId) return null;
    return (
      plans.find(
        (p) => p.plan_id === currentPlanId || p.id === currentPlanId,
      ) || null
    );
  }, [navPlan, plans, currentPlanId]);

  const resolvedDuration = useMemo(() => {
    if (navModuleIds.length > 0) return navDuration;
    if (navDuration) return navDuration;
    if (!resolvedPlan || !Array.isArray(resolvedPlan.durations)) return null;
    if (!currentDurationId) return null;
    return (
      resolvedPlan.durations.find((d) => d.id === currentDurationId) || null
    );
  }, [navDuration, resolvedPlan, currentDurationId]);

  const normalizedRouteStep = useMemo(() => {
    const raw = params?.step;
    if (!raw) return null;
    return BANK_TRANSFER_STEPS.includes(raw) ? raw : null;
  }, [params?.step]);

  const computedAutoStep = useMemo(() => {
    if (isUpgradeBankTransfer) {
      const matchesUpgradeReference =
        !!effectiveStoredUpgradePaymentRef &&
        !!currentPaymentReference &&
        effectiveStoredUpgradePaymentRef === currentPaymentReference;

      if (
        matchesUpgradeReference &&
        currentPaymentMethod === "Bank Transfer" &&
        currentPaymentVerificationStatus === "Rejected"
      ) {
        return "rejected";
      }

      if (
        matchesUpgradeReference &&
        currentPaymentMethod === "Bank Transfer" &&
        currentPaymentVerificationStatus === "Approved" &&
        subscribed
      ) {
        return "approved";
      }

      if (normalizedRouteStep === "pending" || effectiveStoredUpgradePaymentRef) {
        return "pending";
      }

      return "form";
    }

    if (
      flowState?.requires_payment_verification &&
      currentPaymentMethod === "Bank Transfer"
    ) {
      if (currentPaymentVerificationStatus === "Rejected") return "rejected";
      if (currentPaymentVerificationStatus === "Approved") return "approved";
      return "pending";
    }

    if (
      subscribed &&
      currentPaymentMethod === "Bank Transfer" &&
      currentPaymentVerificationStatus === "Approved"
    ) {
      return "approved";
    }

    return "form";
  }, [
    isUpgradeBankTransfer,
    effectiveStoredUpgradePaymentRef,
    normalizedRouteStep,
    flowState?.requires_payment_verification,
    currentPaymentMethod,
    currentPaymentVerificationStatus,
    currentPaymentReference,
    subscribed,
  ]);

  const baseStep = useMemo(() => {
    if (!normalizedRouteStep) {
      return computedAutoStep;
    }

    // If the user is on "rejected", don't auto-downgrade back to "pending".
    // Only allow moving forward (e.g. to "approved") or resetting to "form".
    if (normalizedRouteStep === "rejected") {
      if (computedAutoStep === "approved") return "approved";
      if (computedAutoStep === "form") return "form";
      return "rejected";
    }

    if (normalizedRouteStep === "form") {
      if (computedAutoStep === "form" || computedAutoStep === "rejected") {
        return "form";
      }
      return computedAutoStep;
    }

    if (normalizedRouteStep === "pending") {
      if (computedAutoStep === "form" || computedAutoStep === "pending") {
        return "pending";
      }
      return computedAutoStep;
    }

    if (normalizedRouteStep === "rejected") {
      if (computedAutoStep === "rejected" || computedAutoStep === "form") {
        return "rejected";
      }
      return computedAutoStep;
    }

    if (normalizedRouteStep === "approved") {
      if (computedAutoStep === "approved") {
        return "approved";
      }
      return computedAutoStep;
    }

    return computedAutoStep;
  }, [normalizedRouteStep, computedAutoStep]);

  const desiredStep = forcedStep || baseStep;

  useEffect(() => {
    if (!forcedStep) return;
    if (forcedStep === computedAutoStep) {
      setForcedStepValue(null);
    }
  }, [forcedStep, computedAutoStep]);

  const [paymentSlip, setPaymentSlip] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverMessage, setServerMessage] = useState(navInitialMessage);
  const [subscriptionInfo, setSubscriptionInfo] = useState(
    navInitialSubscription ||
      (currentPaymentReference
        ? { payment_reference: currentPaymentReference }
        : null),
  );
  const [checkingStatus, setCheckingStatus] = useState(false);
  const step = desiredStep;

  useEffect(() => {
    if (!isUpgradeBankTransfer) return;
    if (!subscribed) return;
    if (flowState?.requires_payment_verification) return;
    if (!effectiveStoredUpgradePaymentRef && normalizedRouteStep === "form") return;

    removeSessionValue(UPGRADE_BANK_TRANSFER_FLAG);
    removeSessionValue(UPGRADE_BANK_TRANSFER_REF);

    if (normalizedRouteStep && normalizedRouteStep !== "approved") {
      navigate("/dashboard", { replace: true });
    }
  }, [
    flowState?.requires_payment_verification,
    effectiveStoredUpgradePaymentRef,
    isUpgradeBankTransfer,
    navigate,
    normalizedRouteStep,
    subscribed,
  ]);

  useEffect(() => {
    if (!navResetBankTransferSession) return;
    if (normalizedRouteStep && normalizedRouteStep !== "form") return;

    removeSessionValue(UPGRADE_BANK_TRANSFER_REF);
    setServerMessage(null);
    setSubscriptionInfo(null);
    setForcedStepValue(null);
    updateNavState({
      initialMessage: null,
      initialSubscription: null,
      forcedStep: null,
      resetBankTransferSession: false,
    });
  }, [navResetBankTransferSession, normalizedRouteStep]);

  useEffect(() => {
    if (!desiredStep) return;
    const targetPath = buildStepPath(desiredStep);
    const currentPath = normalizePath(location.pathname);
    const normalizedTarget = normalizePath(targetPath);
    if (currentPath !== normalizedTarget) {
      navigate(targetPath, {
        replace: true,
        state: navStateRef.current,
      });
    }
  }, [desiredStep, location.pathname, navigate]);

  useEffect(() => {
    setServerMessage((prev) =>
      prev === navInitialMessage ? prev : navInitialMessage,
    );
    if (typeof navInitialMessage !== "undefined") {
      updateNavState({ initialMessage: navInitialMessage });
    }
  }, [navInitialMessage]);

  useEffect(() => {
    if (navInitialSubscription) {
      setSubscriptionInfo((prev) => {
        if (prev === navInitialSubscription) return prev;
        if (
          prev?.payment_reference &&
          navInitialSubscription?.payment_reference &&
          prev.payment_reference === navInitialSubscription.payment_reference
        ) {
          return prev;
        }
        return navInitialSubscription;
      });
      updateNavState({ initialSubscription: navInitialSubscription });
    }
  }, [navInitialSubscription]);

  const goToStep = useCallback(
    (value, options = {}) => {
      const target = BANK_TRANSFER_STEPS.includes(value) ? value : "form";
      const path = buildStepPath(target);
      navigate(path, {
        replace: options.replace ?? false,
        state: navStateRef.current,
      });
    },
    [navigate],
  );

  const planForForm = resolvedPlan;
  const durationForForm = resolvedDuration;

  const isAuthorizedOnly =
    !!user &&
    (user.role === "authorized_user" ||
      user.auth_context === "authorized_user");

  const needsVat = useMemo(() => {
    if (!user || isAuthorizedOnly) return false;

    const registeredEmpty =
      !user.VAT_Registered || String(user.VAT_Registered).trim() === "";
    const numberEmpty =
      !user.VAT_Number || String(user.VAT_Number).trim() === "";

    return registeredEmpty && numberEmpty;
  }, [user, isAuthorizedOnly]);

  useEffect(() => {
    if (
      step === "form" &&
      ((!planForForm || !durationForForm) && navModuleIds.length === 0)
    ) {
      navigate("/subscribe", { replace: true });
    }
  }, [step, planForForm, durationForForm, navigate]);

  const approvedSeenKey = useMemo(
    () => (user?.uuid ? `bank_transfer_approved_seen_${user.uuid}` : null),
    [user?.uuid],
  );

  useEffect(() => {
    if (step !== "approved") return;
    if (approvedSeenKey) {
      sessionStorage.setItem(approvedSeenKey, "1");
    }
  }, [step, approvedSeenKey]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (navModuleIds.length === 0 && (!planForForm || !durationForForm)) {
      navigate("/subscribe", { replace: true });
      return;
    }
    if (navModuleIds.length > 0 && !durationForForm?.id) {
      navigate("/subscribe", { replace: true });
      return;
    }

    if (!paymentSlip || !(paymentSlip instanceof File)) {
      Swal.fire({
        text: t("payment.bank_transfer_file_required"),
        icon: "warning",
        iconColor: "#dc2626",
        confirmButtonText: t("OK"),
      });
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (paymentSlip.size > maxSize) {
      Swal.fire({
        text:
          t("payment.bank_transfer_file_too_large") ||
          t("payment.init_error_message"),
        icon: "warning",
        iconColor: "#dc2626",
        confirmButtonText: t("OK"),
      });
      return;
    }

    try {
      setSubmitting(true);
      const res = await dispatch(
        navModuleIds.length > 0
          ? moduleBankTransferSubscribe({
              module_ids: navModuleIds,
              duration_id: durationForForm?.id,
              payment_slip: paymentSlip,
            })
          : navUpgrade
          ? upgradeBankTransferSubscribe({
              plan_id: planForForm.plan_id || planForForm.id,
              duration_id: durationForForm.id,
              payment_slip: paymentSlip,
            })
          : bankTransferSubscribe({
              plan_id: planForForm.plan_id || planForForm.id,
              duration_id: durationForForm.id,
              payment_slip: paymentSlip,
            }),
      ).unwrap();

      const responseMessage = res?.message || null;
      const subscriptionPayload = res?.subscription || null;
      const paymentReference = subscriptionPayload?.payment_reference || null;
      if (isUpgradeBankTransfer && paymentReference) {
        writeSessionValue(UPGRADE_BANK_TRANSFER_REF, paymentReference);
      }
      setServerMessage(responseMessage);
      setSubscriptionInfo(subscriptionPayload);
      updateNavState({
        initialMessage: responseMessage,
        initialSubscription: subscriptionPayload,
      });
      setForcedStepValue("pending");
      goToStep("pending", { replace: true });
      try {
        await dispatch(checkSubscription()).unwrap();
      } catch {
        // ignore
      } finally {
        setForcedStepValue(null);
      }
    } catch (err) {
      const message =
        err?.error ||
        err?.message ||
        (typeof err === "string" ? err : null) ||
        t("payment.bank_transfer_error_generic");

      Swal.fire({
        text: message,
        icon: "warning",
        iconColor: "#dc2626",
        confirmButtonText: t("OK"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToPlans = () => {
    removeSessionValue(UPGRADE_BANK_TRANSFER_FLAG);
    removeSessionValue(UPGRADE_BANK_TRANSFER_REF);
    markSubscribeOverride();
    navigate(isUpgradeBankTransfer ? "/upgrade" : "/subscribe");
  };

  const handleRetryBankTransfer = () => {
    clearSubscribeOverride();
    removeSessionValue(UPGRADE_BANK_TRANSFER_REF);
    goToStep("form", { replace: true });
    setServerMessage(null);
    setSubscriptionInfo(null);
    updateNavState({ initialMessage: null, initialSubscription: null });
  };

  const handleCheckStatus = useCallback(async () => {
    if (step !== "pending") return;
    if (checkingStatusRef.current) return;

    try {
      checkingStatusRef.current = true;
      setCheckingStatus(true);
      const res = await dispatch(checkSubscription()).unwrap();
      const latestSubscription = res?.latest_subscription || null;
      const upgradeStatusSource =
        isUpgradeBankTransfer &&
        effectiveStoredUpgradePaymentRef &&
        latestSubscription?.payment_reference === effectiveStoredUpgradePaymentRef
          ? latestSubscription
          : res;

      const status = upgradeStatusSource?.payment_verification_status;
      if (upgradeStatusSource?.payment_method === "Bank Transfer") {
        if (status === "Approved" || upgradeStatusSource?.status === "Active") {
          if (isUpgradeBankTransfer) {
            removeSessionValue(UPGRADE_BANK_TRANSFER_FLAG);
            removeSessionValue(UPGRADE_BANK_TRANSFER_REF);
          }
          goToStep("approved", { replace: true });
          if (approvedSeenKey) {
            sessionStorage.setItem(approvedSeenKey, "1");
          }
        } else if (status === "Rejected") {
          const rejectionMessage = upgradeStatusSource?.message || serverMessage;
          const nextReference = upgradeStatusSource?.payment_reference
            ? { payment_reference: upgradeStatusSource.payment_reference }
            : subscriptionInfo;
          setServerMessage(rejectionMessage);
          setSubscriptionInfo(nextReference);
          updateNavState({
            initialMessage: rejectionMessage,
            initialSubscription: nextReference,
          });
          goToStep("rejected", { replace: true });
        } else {
          pollDelayRef.current = Math.min(
            Math.round(pollDelayRef.current * 1.5),
            120_000,
          );
        }
      }
    } catch {
      // user can retry later
    } finally {
      checkingStatusRef.current = false;
      setCheckingStatus(false);
    }
  }, [
    approvedSeenKey,
    dispatch,
    goToStep,
    isUpgradeBankTransfer,
    serverMessage,
    step,
    effectiveStoredUpgradePaymentRef,
    subscriptionInfo,
  ]);

  useEffect(() => {
    if (step !== "pending") return;

    pollDelayRef.current = 30_000;

    const tick = () => {
      if (document.visibilityState && document.visibilityState !== "visible") {
        scheduleNext();
        return;
      }
      handleCheckStatus().finally(() => {
        scheduleNext();
      });
    };

    const scheduleNext = () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
      pollTimeoutRef.current = setTimeout(tick, pollDelayRef.current);
    };

    tick();

    const onFocus = () => {
      if (focusDebounceRef.current) {
        clearTimeout(focusDebounceRef.current);
      }
      focusDebounceRef.current = setTimeout(() => {
        pollDelayRef.current = 30_000;
        tick();
      }, 750);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      onFocus();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
      pollTimeoutRef.current = null;
      if (focusDebounceRef.current) {
        clearTimeout(focusDebounceRef.current);
      }
      focusDebounceRef.current = null;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [handleCheckStatus, step]);

  const planName =
    navModuleIds.length > 0
      ? navModuleSummary || t("payment.modules_placeholder")
      : planForForm
      ? i18n.language === "ar"
        ? planForForm.plan_name_ar
        : planForForm.plan_name_en
      : "";

  const amount =
    navModuleIds.length > 0
      ? Number(navModuleAmount || 0)
      : durationForForm
      ? Number(durationForForm?.price || 0) || 0
      : 0;

  const getDurationLabel = (dur) => {
    const raw = String(
      dur?.duration_type || dur?.type || dur?.Duration_Type || "",
    );
    const key = raw.trim().toLowerCase();

    const map = {
      "45 days": { en: "45 Days", ar: "45 يوم" },
      "3 months": { en: "3 Months", ar: "3 أشهر" },
      "6 months": { en: "6 Months", ar: "6 أشهر" },
      "1 year": { en: "1 Year", ar: "سنة واحدة" },
    };

    const lang = i18n.language === "ar" ? "ar" : "en";
    return map[key]?.[lang] || raw;
  };

  const handlePrint = () => {
    try {
      const container = document.getElementById("bank-transfer-page-card");
      if (!container) return;

      const printWindow = window.open("", "_blank", "width=900,height=650");
      if (!printWindow) return;

      const headHtml = document.head?.innerHTML || "";

      printWindow.document.open();
      printWindow.document.write(
        `<html dir="${i18n.language === "ar" ? "rtl" : "ltr"}">
            <head>
             <title>${PRINT_HEADER_TITLE}</title>
             ${headHtml}
           </head>
          <body>
            <div style="margin: 24px;">
              ${container.outerHTML}
            </div>
          </body>
        </html>`,
      );
      printWindow.document.close();

      const handleLoaded = () => {
        setTimeout(() => {
          try {
            printWindow.focus();
            printWindow.print();
          } catch {
            // ignore
          }
        }, 400);
      };

      if (printWindow.document.readyState === "complete") {
        handleLoaded();
      } else {
        printWindow.onload = handleLoaded;
      }
    } catch {
      // ignore print failures
    }
  };

  const renderBankDetails = () => (
    <div className="bg-gray-50 dark:bg-slate-800 rounded-2xl p-4 border border-gray-100 dark:border-slate-700 space-y-3">
      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-50 text-[#1B8354]">
          <Banknote className="w-4 h-4" />
        </span>
        {t("payment.bank_details_title")}
      </h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-gray-700 dark:text-gray-200">
        <div>
          <dt className="font-semibold">
            {t("payment.bank_account_name_label")}
          </dt>
          <dd className="mt-0.5">{t("payment.bank_account_name_value")}</dd>
        </div>

        <div>
          <dt className="font-semibold">{t("unifiedNumberTitle")}</dt>
          <dd className="mt-0.5">{t("unifiedNumberValue")}</dd>
        </div>

        <div>
          <dt className="font-semibold">{t("vat_number_label")}</dt>
          <dd className="mt-0.5">{t("vat_number_value")}</dd>
        </div>

        <div>
          <dt className="font-semibold">{t("payment.bank_name_label")}</dt>
          <dd className="mt-0.5">{t("payment.bank_name_value")}</dd>
        </div>

        <div>
          <dt className="font-semibold">{t("payment.bank_iban_label")}</dt>
          <dd className="mt-0.5">{t("payment.bank_iban_value")}</dd>
        </div>

        <div>
          <dt className="font-semibold">{t("national_address")}</dt>
          <dd className="mt-0.5">{t("national_address_value")}</dd>
        </div>
      </dl>
    </div>
  );

  const renderFormStep = () => (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="bg-gray-50 dark:bg-slate-800 rounded-2xl p-4 border border-gray-100 dark:border-slate-700 space-y-2">
        <div className="flex justify-between text-xs text-gray-600 dark:text-gray-300">
          <span>{t("payment.plan_label")}</span>
          <span className="font-semibold text-gray-900 dark:text-white">
            {planName}
          </span>
        </div>
        <div className="flex justify-between text-xs text-gray-600 dark:text-gray-300">
          <span>{t("payment.duration_label")}</span>
          <span className="font-semibold text-gray-900 dark:text-white">
            {getDurationLabel(durationForForm) || "-"}
          </span>
        </div>
        <div className="flex justify-between items-baseline pt-2 border-t border-dashed border-gray-200 dark:border-slate-700 mt-2">
          <span className="text-xs text-gray-600 dark:text-gray-300">
            {t("payment.total_label")}
          </span>
          <div className="flex flex-col gap-0.5">
            <div className="flex justify-end items-baseline gap-1">
              <span className="text-xl font-extrabold text-[#1B8354]">
                {amount.toLocaleString("en-US")}
              </span>
              <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                <SaudiRiyal size={14} />
              </span>
            </div>
            <span className="text-[6.5px] font-semibold text-gray-500 dark:text-gray-400">
              {t("withVAT")}
            </span>
          </div>
        </div>
      </div>

      {renderBankDetails()}

      <div className="space-y-2">
        <label className="block text-sm font-semibold text-gray-800 dark:text-white">
          {t("payment.bank_transfer_upload_label")}
        </label>
        <div className="flex items-center gap-3">
          <input
            id="bank-transfer-slip-upload-page"
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.svg,application/pdf,image/png,image/jpeg,image/jpg,image/svg+xml"
            className="hidden"
            onChange={(e) => setPaymentSlip(e.target.files?.[0] || null)}
          />
          <label
            htmlFor="bank-transfer-slip-upload-page"
            className="cursor-pointer inline-flex items-center justify-center px-4 py-2 rounded-xl bg-gradient-to-r from-[#1B8354] to-[#22a066] text-white text-sm font-semibold shadow-md hover:from-[#146b43] hover:to-[#1B8354] transition-all"
          >
            {t("browse")}
          </label>
          <span className="text-xs text-gray-600 dark:text-gray-300 truncate max-w-[220px]">
            {paymentSlip
              ? paymentSlip.name
              : t("payment.bank_transfer_upload_placeholder") ||
                t("upload_file")}
          </span>
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          {t("payment.bank_transfer_file_hint")}
        </p>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={handleBackToPlans}
          className="px-4 py-2 rounded-xl border border-gray-300 dark:border-slate-600 text-xs font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition"
          disabled={submitting}
        >
          {t("cancel")}
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-5 py-2 rounded-xl bg-gradient-to-r from-[#1B8354] to-[#22a066] text-white text-sm font-semibold shadow-md hover:from-[#146b43] hover:to-[#1B8354] disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {submitting && (
            <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
          )}
          <span>{t("payment.bank_transfer_submit")}</span>
        </button>
      </div>
    </form>
  );

  const renderPendingStep = () => (
    <div className="space-y-4 text-center">
      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
        {t("payment.bank_transfer_pending_title")}
      </h3>
      {/* {serverMessage && (
        <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed max-w-md mx-auto">
          {serverMessage}
        </p>
      )} */}
      <p className="text-[14px] text-gray-500 dark:text-gray-400">
        {t("payment.locked_until_approved")}
      </p>
      <button
        type="button"
        onClick={handleCheckStatus}
        disabled={checkingStatus}
        className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-semibold bg-[#1B8354] text-white hover:bg-[#146b43] disabled:opacity-60 disabled:cursor-not-allowed transition"
      >
        {checkingStatus
          ? t("payment.bank_transfer_checking_status")
          : t("payment.bank_transfer_check_status_button")}
      </button>
    </div>
  );

  const renderApprovedStep = () => (
    <div className="space-y-4 text-center">
      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
        {t("payment.bank_transfer_approved_title")}
      </h3>
      <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed max-w-md mx-auto">
        {t("payment.bank_transfer_approved_message")}
      </p>

      {needsVat && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          {t("payment.bank_transfer_vat_note")}
        </p>
      )}
      <div className="flex flex-wrap gap-3 justify-center">
        {/* <button
          type="button"
          onClick={() => navigate("/dashboard", { replace: true })}
          className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-semibold bg-[#1B8354] text-white hover:bg-[#146b43] transition"
        >
          {t("dashboard")}
        </button> */}

        <button
          type="button"
          onClick={() => {
            removeSessionValue(UPGRADE_BANK_TRANSFER_FLAG);
            removeSessionValue(UPGRADE_BANK_TRANSFER_REF);
            navigate("/vat");
          }}
          className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-semibold border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-slate-800 transition"
        >
          {t("continue")}
        </button>
      </div>
    </div>
  );

  const renderRejectedStep = () => (
    <div className="space-y-4 text-center">
      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
        {t("payment.bank_transfer_rejected_title")}
      </h3>
      <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed max-w-md mx-auto">
        {serverMessage || t("payment.bank_transfer_rejected_message")}
      </p>
      <p className="text-[11px] text-gray-500 dark:text-gray-400">
        {t("payment.bank_transfer_rejected_hint")}
      </p>
      <div className="flex flex-wrap gap-3 justify-center">
        <button
          type="button"
          onClick={handleRetryBankTransfer}
          className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-semibold bg-[#1B8354] text-white hover:bg-[#146b43] transition"
        >
          {t("payment.bank_transfer_retry_button")}
        </button>
        <button
          type="button"
          onClick={handleBackToPlans}
          className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-semibold border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-slate-800 transition"
        >
          {t("payment.bank_transfer_change_method_button")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-slate-900 py-5 px-2">
      <div className="max-w-4xl mx-auto space-y-2">
        <div className="flex items-center justify-between">
          {["form", "rejected"].includes(step) ? (
            <button
              type="button"
              onClick={handleBackToPlans}
              className="text-xs font-semibold text-[#1B8354] hover:text-[#166c44] transition underline"
            >
              {t("back_to_plans") || t("back")}
            </button>
          ) : (
            <span />
          )}
          {step === "form" && (
            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition"
            >
              <Printer className="w-4 h-4" />
            </button>
          )}
        </div>

        <div
          id="bank-transfer-page-card"
          className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-white/10 dark:border-slate-800/60 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-[#1B8354] to-[#22a066] px-6 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center">
              <Banknote className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-base">
                {t("payment.bank_transfer_title")}
              </h2>
              {step === "form" && (
                <p className="text-white/80 text-[11px]">
                  {t("payment.bank_transfer_subtitle")}
                </p>
              )}
            </div>
          </div>

          <div className="p-6 space-y-5">
            {step === "form" &&
              ((navModuleIds.length > 0 && durationForForm) ||
                (planForForm && durationForForm)) && (
                <div>{renderFormStep()}</div>
              )}
            {step === "pending" && renderPendingStep()}
            {step === "approved" && renderApprovedStep()}
            {step === "rejected" && renderRejectedStep()}
          </div>
        </div>
      </div>
    </div>
  );
}
